import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Globe, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const userMarkerIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #df1283, #FF7901);
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const WorldMap = ({ locations }) => {
  // Group by state/region AND city
  const locationGroups = locations.reduce((acc, user) => {
    if (!user.location?.city) return acc;
    const state = user.location.region || user.location.country;
    const city = user.location.city;
    const country = user.location.country;
    const key = `${city}, ${state}`;
    if (!acc[key]) acc[key] = { city, state, country, users: [], lat: user.location.lat, lon: user.location.lon };
    acc[key].users.push(user);
    return acc;
  }, {});

  // Calculate center based on users or default to world center
  const getCenter = () => {
    if (locations.length === 0) return [20, 0];
    const validLocs = locations.filter(u => u.location?.lat && u.location?.lon);
    if (validLocs.length === 0) return [20, 0];
    const avgLat = validLocs.reduce((sum, u) => sum + u.location.lat, 0) / validLocs.length;
    const avgLon = validLocs.reduce((sum, u) => sum + u.location.lon, 0) / validLocs.length;
    return [avgLat, avgLon];
  };

  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 col-span-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#df1283]" />
          Global User Distribution
        </h3>
        <span className="text-xs text-[#FDF2F8]/50">
          {locations.length} users • {Object.keys(locationGroups).length} locations
        </span>
      </div>
      
      {locations.length === 0 ? (
        <div className="text-center py-8 text-[#FDF2F8]/40 text-sm">
          Location data will appear as users log in
        </div>
      ) : (
        <>
          {/* Real Leaflet Map with dark theme and bright labels */}
          <div className="rounded-xl overflow-hidden mb-4 border-2 border-[#df1283] shadow-lg shadow-[#df1283]/20" style={{ height: '500px' }}>
            <style>{`
              .leaflet-container {
                background: #1a0527 !important;
              }
              .leaflet-tile-pane {
                filter: brightness(1.3) contrast(1.1);
              }
              .leaflet-control-zoom a {
                background: linear-gradient(135deg, #df1283, #FF7901) !important;
                color: white !important;
                border: none !important;
                font-weight: bold !important;
              }
              .leaflet-control-zoom a:hover {
                background: linear-gradient(135deg, #FF7901, #df1283) !important;
              }
              .leaflet-control-attribution {
                display: none !important;
              }
              .leaflet-popup-content-wrapper {
                background: linear-gradient(135deg, #2D063E, #4A0A66) !important;
                color: white !important;
                border: 2px solid #df1283 !important;
              }
              .leaflet-popup-tip {
                background: #4A0A66 !important;
              }
            `}</style>
            <MapContainer 
              center={getCenter()} 
              zoom={2} 
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              {locations.map((user, idx) => {
                if (!user.location?.lat || !user.location?.lon) return null;
                return (
                  <Marker 
                    key={user.user_id || idx}
                    position={[user.location.lat, user.location.lon]}
                    icon={userMarkerIcon}
                  >
                    <Popup>
                      <div className="text-center p-1">
                        <p className="font-bold text-white text-sm">{user.display_name || user.name || 'User'}</p>
                        <p className="text-[#FF7901] text-xs font-medium">{user.location.city}</p>
                        <p className="text-[#df1283] text-xs">{user.location.region}</p>
                        <p className="text-white/70 text-xs">{user.location.country}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          
          {/* Location list with city, state, country */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(locationGroups)
              .sort((a, b) => b[1].users.length - a[1].users.length)
              .slice(0, 10)
              .map(([key, data]) => (
                <div 
                  key={key}
                  className="bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 border border-[#df1283]/40 rounded-lg px-3 py-2 flex items-center gap-2 hover:border-[#df1283] transition-colors"
                >
                  <MapPin className="w-4 h-4 text-[#df1283]" />
                  <div>
                    <p className="text-xs text-white font-medium">{data.city}</p>
                    <p className="text-[10px] text-[#FF7901]">{data.state}, {data.country}</p>
                  </div>
                  <span className="text-sm text-[#df1283] font-bold ml-1">{data.users.length}</span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default WorldMap;
